import { ApplicationSettings, FeatureManagementType } from "@api/apiClient";
import columnConfig from "@configs/material-columns.json";

export type ClientConfigKey = keyof typeof columnConfig;

export interface ClientConfigOptions {
  fallbackToDefault?: boolean;
  logWarnings?: boolean;
}

/**
 * Client configuration mapping
 * Add new clients here as they are implemented
 */
const CLIENT_FEATURE_MAP: Record<string, FeatureManagementType> = {
  ADStages: FeatureManagementType.ADStages,
  // Add more clients here as needed
  // wsp: FeatureManagementType.Wsp,
};

/**
 * Determines the appropriate client configuration key based on app settings
 * @param appSettings - The application settings object
 * @param options - Configuration options
 * @returns The client configuration key
 */
export const getClientConfigKey = (
  appSettings: ApplicationSettings | null,
  options: ClientConfigOptions = {}
): ClientConfigKey => {
  const { fallbackToDefault = true, logWarnings = true } = options;

  const features = appSettings?.features || [];

  // Check each client in priority order
  for (const [clientKey, featureType] of Object.entries(CLIENT_FEATURE_MAP)) {
    if (features.includes(featureType)) {
      if (logWarnings && !columnConfig[clientKey as ClientConfigKey]) {
        console.warn(`Client configuration '${clientKey}' is enabled but configuration not found`);
      }
      return clientKey as ClientConfigKey;
    }
  }

  // Return default if fallback is enabled
  if (fallbackToDefault) {
    return "Default";
  }

  throw new Error("No valid client configuration found and fallback disabled");
};

/**
 * Gets the column configuration for a specific client
 * @param clientKey - The client configuration key
 * @param fallbackToDefault - Whether to fallback to default if client config not found
 * @returns The column configuration or null if not found
 */
export const getClientColumnConfig = (
  clientKey: ClientConfigKey,
  fallbackToDefault: boolean = true
) => {
  const config = columnConfig[clientKey];

  if (config) {
    return config;
  }

  if (fallbackToDefault && clientKey !== "Default") {
    console.warn(`Configuration for '${clientKey}' not found, falling back to default`);
    return columnConfig["Default"];
  }

  return null;
};

/**
 * Determines if a specific client feature is enabled
 * @param appSettings - The application settings object
 * @param clientKey - The client to check
 * @returns Whether the client feature is enabled
 */
export const isClientEnabled = (appSettings: ApplicationSettings | null, clientKey: string): boolean => {
  const featureType = CLIENT_FEATURE_MAP[clientKey];
  if (!featureType) {
    return false;
  }

  const features = appSettings?.features || [];
  return features.includes(featureType);
};

/**
 * Helper function to check if ADStages features are enabled
 * @param appSettings - The application settings object
 * @returns Whether ADStages features are enabled
 */
export const isADStagesEnabled = (appSettings: ApplicationSettings | null): boolean => {
  return isClientEnabled(appSettings, 'ADStages');
};

/**
 * Helper function to check if current client should show MPA tabs
 * @param appSettings - The application settings object
 * @returns Whether MPA tabs should be shown
 */
export const showMPATabs = (appSettings: ApplicationSettings | null): boolean => {
  return isADStagesEnabled(appSettings);
};

/**
 * Helper function to check if materials should be set as default on import
 * @param appSettings - The application settings object
 * @returns Whether imported materials should be marked as default
 */
export const setMaterialsAsDefault = (appSettings: ApplicationSettings | null): boolean => {
  return isADStagesEnabled(appSettings);
};

/**
 * Gets all enabled client keys
 * @param appSettings - The application settings object
 * @returns Array of enabled client keys
 */
export const getEnabledClients = (appSettings: ApplicationSettings | null): string[] => {
  const features = appSettings?.features || [];

  return Object.entries(CLIENT_FEATURE_MAP)
    .filter(([, featureType]) => features.includes(featureType))
    .map(([clientKey]) => clientKey);
};

/**
 * Gets all available client configuration keys
 * @returns Array of all available client keys
 */
export const getAvailableClients = (): ClientConfigKey[] => {
  return Object.keys(columnConfig) as ClientConfigKey[];
};

/**
 * Validates if a client configuration is complete and valid
 * @param clientKey - The client configuration key to validate
 * @returns Validation result with details
 */
export const validateClientConfig = (clientKey: ClientConfigKey) => {
  const config = columnConfig[clientKey];

  if (!config) {
    return {
      isValid: false,
      errors: [`Configuration not found for client: ${clientKey}`],
      warnings: [],
    };
  }

  const errors: string[] = [];
  const warnings: string[] = [];

  // Check if columns exist
  if (!config.columns || !Array.isArray(config.columns)) {
    errors.push(`No columns defined for client: ${clientKey}`);
  } else if (config.columns.length === 0) {
    warnings.push(`No columns configured for client: ${clientKey}`);
  }

  // Check for required fields in columns
  config.columns?.forEach((column: any, index: number) => {
    if (!column.field) {
      errors.push(`Column at index ${index} missing required 'field' property`);
    }
    if (!column.headerNameKey) {
      warnings.push(`Column '${column.field}' missing 'headerNameKey' for i18n`);
    }
  });

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
};

/**
 * Gets detailed information about the current client configuration
 * @param appSettings - The application settings object
 * @returns Detailed client configuration information
 */
export const getClientConfigInfo = (appSettings: ApplicationSettings | null) => {
  const clientKey = getClientConfigKey(appSettings);
  const config = getClientColumnConfig(clientKey);
  const enabledClients = getEnabledClients(appSettings);
  const validation = validateClientConfig(clientKey);

  return {
    clientKey,
    config,
    enabledClients,
    availableClients: getAvailableClients(),
    validation,
    columnCount: config?.columns?.length || 0,
    features: appSettings?.features || [],
  };
};

/**
 * Debug utility to log client configuration information
 * @param appSettings - The application settings object
 */
export const debugClientConfig = (appSettings: ApplicationSettings | null): void => {
  const info = getClientConfigInfo(appSettings);

  console.group('🔧 Client Configuration Debug');
  console.log('Current Client:', info.clientKey);
  console.log('Enabled Clients:', info.enabledClients);
  console.log('Available Clients:', info.availableClients);
  console.log('Column Count:', info.columnCount);
  console.log('Features:', info.features);

  if (info.validation.warnings.length > 0) {
    console.warn('⚠️ Warnings:', info.validation.warnings);
  }

  if (!info.validation.isValid) {
    console.error('❌ Validation Errors:', info.validation.errors);
  } else {
    console.log('✅ Configuration is valid');
  }

  console.groupEnd();
}; 